import { useMemo, useState } from 'react';
import { lookupRoundAt, type Btc15History } from '../../lib/btc15History';
import disclosure from './Btc15Details.module.css';
import styles from './Btc15RoundLookup.module.css';

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function formatWindow(startMs: number, closeMs: number): string {
  const start = new Date(startMs);
  const close = new Date(closeMs);
  const time = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const sameDayAsToday = start.toDateString() === new Date().toDateString();
  const dateLabel = sameDayAsToday
    ? ''
    : `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · `;
  return `${dateLabel}${time(start)} – ${time(close)}`;
}

/**
 * A local-time round lookup, independent of the live "Recent" chips on the
 * decision card (which only ever show the last few funded results). This
 * lets the person pick any date/time in their own timezone and see whether
 * that specific 15-minute round was funded and how it turned out, reusing
 * the exact epoch-aligned round boundaries recording uses (see
 * `lookupRoundAt` / `currentRound`) so the match is exact, not approximate.
 */
export function Btc15RoundLookup({ history }: { history: Btc15History }) {
  const [value, setValue] = useState(() => toDatetimeLocalValue(new Date()));

  const result = useMemo(() => {
    if (!value) return null;
    const ms = new Date(value).getTime();
    if (Number.isNaN(ms)) return null;
    return lookupRoundAt(history, ms);
  }, [history, value]);

  return (
    // A collapsed row like "Full model details" below it, not a card: it's
    // secondary to the live round.
    <details className={disclosure.details}>
      <summary className={disclosure.summary}>Look up a past round</summary>
      <div className={`${disclosure.body} ${styles.body}`}>
        <p className={styles.hint}>
          Pick a local date and time to see whether that round was funded and how it
          turned out.
        </p>

        <input
          type="datetime-local"
          className={styles.input}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          aria-label="Local date and time to look up"
        />

        {result && (
          <div className={styles.result}>
            <span className={styles.window}>
              {formatWindow(result.roundStartMs, result.roundCloseMs)}
            </span>
            {result.status === 'no-data' && (
              <span className={styles.statusMuted}>
                No recorded round for this window.
              </span>
            )}
            {result.status === 'unsettled' && (
              <span className={styles.statusMuted}>
                This round hasn&apos;t settled yet.
              </span>
            )}
            {result.status === 'not-funded' && (
              <span className={styles.statusMuted}>
                Not funded —{' '}
                {result.recommendation === 'SKIP'
                  ? 'the model skipped this round'
                  : 'sized below the minimum stake'}
                .
              </span>
            )}
            {result.status === 'funded' && (
              <span className={result.won ? styles.win : styles.loss}>
                Funded · {result.action} · {result.won ? 'Won' : 'Lost'}
                {!result.official && ' (provisional)'}
              </span>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
